import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Shield, ShieldCheck, Info, Settings } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { useLabel } from '@/lib/labels';

type Role = {
  role_id: string;
  tenant_id: string | null;
  role_name: string;
  description: string | null;
  is_system_role: boolean;
  is_system_reserved?: boolean;
  assigned_user_count?: number;
};

type Permission = {
  permission_id: string;
  feature: string;
  action: 'none' | 'view' | 'create' | 'update' | 'delete' | 'edit' | 'manage';
  scope: 'platform' | 'all_stores' | 'multi_store' | 'own_store' | 'node_scope';
  store_ids: string[] | null;
  node_ids: string[] | null;
};

const roleLabels: Record<string, { label: string; desc: string }> = {
  platform_admin: {
    label: 'プラットフォーム管理者',
    desc: 'システム提供側スタッフ。全テナント横断でアクセス可',
  },
  company_admin: {
    label: '会社管理者',
    desc: '会社（テナント）全体・全店舗の管理者。ユーザー登録、マスタ編集、CSV取込等が可能',
  },
  manager: {
    label: 'マネージャー',
    desc: '担当店舗のダッシュボード・マスタ閲覧が可能。編集・取込は不可',
  },
};

const featureLabels: Record<string, string> = {
  dashboard: 'ダッシュボード',
  store_master: '店舗マスタ',
  user_master: 'ユーザーマスタ',
  menu_master: 'メニューマスタ',
  ingredient_master: '食材マスタ',
  cost_category: 'コスト分類マスタ',
  kpi_threshold: 'KPI閾値マスタ',
  csv_import: 'CSV/手入力データ取込',
  csv_export: 'データエクスポート',
  audit_log: '監査ログ',
  tenant_master: 'テナント管理',
  // v0.4 追加要件⑪ (migration 0107): シフトレポート (横断参照) の表示項目・計算式カタログ管理
  report_view: 'シフトレポート表示設定',
  // v0.5⑧ 経営会議PDCA (migration 0164): §3 登録箇所6番目 (2つ目のfeatureLabels辞書、§8-152型)。
  board_meeting: '経営会議',
  board_initiative: '施策台帳',
  // v0.4後半 月次実績一括入力 第2フェーズ I-6 出力機能 (migration 0189、§8-152型): 2つ目のfeatureLabels辞書。
  attendance_export: '勤怠実績の出力',
};

const actionLabels: Record<string, string> = {
  none: 'なし',
  view: '閲覧',
  create: '作成',
  update: '更新',
  delete: '削除',
  edit: '編集 (旧・作成/更新/削除相当)',
  manage: '管理',
};

const scopeLabels: Record<string, string> = {
  platform: '全テナント',
  all_stores: '自テナント全店',
  multi_store: '指定複数店舗',
  own_store: '自所属のみ',
  node_scope: '指定ノード配下',
};

export function RolesPage() {
  const t = useLabel();
  const effectivePermissions = useAuthStore((s) => s.user?.effective_permissions);
  const canManageRoles = hasPermission(effectivePermissions, 'role', 'manage');
  const { data: roles = [], isLoading } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => apiFetch<Role[]>('/roles'),
  });
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const { data: permissions = [] } = useQuery<Permission[]>({
    queryKey: ['role-permissions', selectedRoleId],
    queryFn: () => apiFetch<Permission[]>(`/roles/${selectedRoleId}/permissions`),
    enabled: !!selectedRoleId,
  });

  const roleInfo = (r: Role) => roleLabels[r.role_name] ?? { label: r.role_name, desc: r.description ?? '' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1">{t('entity.roles', 'ロール')}・権限管理</h1>
          <p className="text-sm text-neutral-sub mt-1">
            システム固定ロール3種とカスタムロールの権限を確認できます
          </p>
        </div>
        {canManageRoles && (
          <Link
            to="/settings/roles"
            className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover"
          >
            <Settings size={16} /> ロールを管理
          </Link>
        )}
      </div>

      {/* 店舗スコープの説明 */}
      <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm text-neutral-sub space-y-2">
        <div className="flex items-start gap-2">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>
            <strong>店舗スコープ制御について：</strong>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li><strong>プラットフォーム管理者・会社管理者</strong>：自テナントの全店舗を閲覧・編集可能</li>
              <li><strong>マネージャー以下</strong>：ユーザー管理画面で割り当てた「担当店舗」の情報のみ閲覧・編集可能</li>
              <li>担当店舗を割り当てるには「ユーザー管理」画面で対象ユーザーを編集</li>
            </ul>
          </div>
        </div>
        {canManageRoles && (
          <div className="flex items-start gap-2 pt-2 border-t border-neutral-line">
            <Info size={16} className="mt-0.5 shrink-0" />
            <div>
              「ロールを管理」からテナント独自のカスタムロール（例：店長／副店長／シェフ／スタッフ）を作成し、機能×アクション×スコープを画面から編集できます。
            </div>
          </div>
        )}
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* ロール一覧 */}
          <div className="lg:col-span-1 space-y-2">
            <h2 className="text-h3 mb-2">ロール一覧</h2>
            {roles.map((r) => {
              const info = roleInfo(r);
              const active = selectedRoleId === r.role_id;
              return (
                <button
                  key={r.role_id}
                  onClick={() => setSelectedRoleId(r.role_id)}
                  className={`w-full text-left border rounded-lg p-3 transition ${
                    active ? 'border-brand-red bg-warning-bg/30' : 'border-neutral-line hover:bg-neutral-panel'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {r.is_system_role ? (
                      <ShieldCheck size={16} className="text-brand-red" />
                    ) : (
                      <Shield size={16} className="text-neutral-sub" />
                    )}
                    <div className="font-medium">{info.label}</div>
                  </div>
                  <div className="text-xs text-neutral-sub mt-1 pl-6">{info.desc}</div>
                  {r.is_system_role && (
                    <div className="text-[10px] text-brand-red mt-1 pl-6">システム固定・変更不可</div>
                  )}
                </button>
              );
            })}
          </div>

          {/* 選択ロールの権限詳細 */}
          <div className="lg:col-span-2">
            <h2 className="text-h3 mb-2">
              権限詳細
              {selectedRoleId && ` — ${roleInfo(roles.find((r) => r.role_id === selectedRoleId) ?? roles[0]!).label}`}
            </h2>
            {selectedRoleId ? (
              <div className="border border-neutral-line rounded-lg bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-panel text-neutral-sub">
                    <tr>
                      <th className="text-left px-4 py-2">機能</th>
                      <th className="text-left px-4 py-2 w-24">アクション</th>
                      <th className="text-left px-4 py-2 w-32">スコープ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((p) => (
                      <tr key={p.permission_id} className="border-t border-neutral-line">
                        <td className="px-4 py-2">{featureLabels[p.feature] ?? p.feature}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                            p.action === 'manage' ? 'bg-brand-red text-white'
                            : p.action === 'delete' ? 'bg-warning-text text-white'
                            : (p.action === 'create' || p.action === 'update' || p.action === 'edit') ? 'bg-brand-orange text-white'
                            : p.action === 'view' ? 'bg-neutral-line text-neutral-text'
                            : 'bg-neutral-panel text-neutral-sub'
                          }`}>
                            {actionLabels[p.action] ?? p.action}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-neutral-sub text-xs">
                          {scopeLabels[p.scope] ?? p.scope}
                          {p.scope === 'multi_store' && p.store_ids && p.store_ids.length > 0 && (
                            <div className="text-[10px]">({p.store_ids.join(', ')})</div>
                          )}
                          {p.scope === 'node_scope' && p.node_ids && (
                            <div className="text-[10px]">{p.node_ids.length === 0 ? '(未指定)' : `(${p.node_ids.length}ノード)`}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {permissions.length === 0 && (
                      <tr><td colSpan={3} className="text-center py-6 text-neutral-sub">権限が設定されていません</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="border border-dashed border-neutral-line rounded-lg p-8 text-center text-neutral-sub">
                左からロールを選択すると、そのロールに紐付く権限が表示されます
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
